<template>
  <div
    class="relative mx-auto mb-16 max-w-[498px] md:mb-0 md:block md:w-full md:min-w-[337px]"
  >
    <RevCarousel
      v-if="slides.length"
      :alternative-button-label="i18n(translations.alternativeController)"
      :alternative-next="i18n(translations.alternativeNext)"
      :alternative-previous="i18n(translations.alternativePrevious)"
      class="w-full"
      current-index-id="condition-guidance"
      @navigation-click="() => trackCarousel('thumbnail_carousel')"
      @next-button-click="() => trackCarousel('right_arrow')"
      @prev-button-click="() => trackCarousel('left_arrow')"
      @slide-impression="onSlideImpression"
      @swipe="() => trackCarousel('swipe')"
    >
      <!-- eslint-disable tailwindcss/no-custom-classname -->
      <div
        v-for="(slide, slideIndex) in slides"
        :key="slideIndex"
        class="grade-gradient-unified rounded-lg relative flex w-full md:rounded-[32px]"
        data-test="condition-guidance-slide"
      >
        <RevIllustration
          ref="illustrations"
          :alt="slide.title || ''"
          class="rounded-lg h-auto w-full md:min-w-[337px] md:max-w-[498px] md:rounded-[32px]"
          :height="498"
          :src="slide.imageUrl"
          :width="498"
          @load="onSlideImageLoaded(slideIndex)"
        />

        <ExampleImage
          class="bg-[#1C1C1CB2] absolute right-16 top-16 px-4 text-static-light-hi backdrop-blur-sm rounded-full"
          :with-icon="false"
        />

        <div
          v-if="showTags && slide.tags"
          class="rounded-b-lg absolute inset-x-0 bottom-0 flex flex-col gap-8 p-16 md:rounded-b-[32px] md:px-24 md:pb-24 md:pt-32"
        >
          <!-- eslint-enable tailwindcss/no-custom-classname -->
          <p
            class="inline-flex self-start items-center rounded-full bg-overlay-low py-2 px-8 text-static-light-hi heading-2 [text-shadow:0_0_4px_rgba(0,0,0,0.5)]"
          >
            {{ slide.title }}
          </p>
          <!-- Animated chips: keyed by index and icon for shell reuse + TransitionGroup for count changes -->
          <TransitionGroup
            v-if="isWithOptimisedGrades"
            class="relative flex grow flex-row flex-wrap gap-8 pt-8"
            :css="false"
            mode="out-in"
            tag="div"
            @before-enter="onBeforeEnter"
            @enter="onEnter"
            @leave="onLeave"
          >
            <template v-for="(tag, tagIndex) in sortedTags" :key="tagIndex">
              <AnimatedPremiumGuidanceChip
                v-if="getLastPremiumTag(tag)"
                :index="tagIndex"
                :tag
              />
              <AnimatedGuidanceChip v-else :index="tagIndex" :tag />
            </template>
          </TransitionGroup>

          <!-- Default chips: no animation -->
          <div v-else class="flex grow flex-row flex-wrap gap-8 pt-8">
            <GuidanceChip v-for="tag in slide.tags" :key="tag.id" :tag />
          </div>
        </div>
      </div>
    </RevCarousel>
    <div
      v-if="shouldDisplayLoader"
      class="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
    >
      <RevSpinner size="large" />
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue'

import type { Picker } from '@backmarket/http-api/src/api-specs-navigation-experience/product/pickers'
import { useExperiments } from '@backmarket/nuxt-module-experiments/useExperiments'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import type { EventData } from '@backmarket/nuxt-module-tracking'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { RevCarousel } from '@ds/components/Carousel'
import { RevIllustration } from '@ds/components/Illustration'
import { RevSpinner } from '@ds/components/Spinner'

import ExampleImage from '~/scopes/product-core/components/presentationals/ExampleImage/ExampleImage.vue'
import { useUrlParams } from '~/scopes/product-core/composables/useUrlParams/useUrlParams'

import translations from './ConditionGuidance.translations'
import AnimatedGuidanceChip from './components/AnimatedGuidanceChip/AnimatedGuidanceChip.vue'
import AnimatedPremiumGuidanceChip from './components/AnimatedPremiumGuidanceChip/AnimatedPremiumGuidanceChip.vue'
import GuidanceChip from './components/GuidanceChip/GuidanceChip.vue'
import { useChipTransitionHooks } from './composables/useChipAnimations'

/** Only show spinner if the image has been loading for longer than this (avoids flash for cached images). */
const LOADER_DELAY_MS = 400

const props = withDefaults(
  defineProps<{
    trackingModel?: string
    brand?: string
    content?:
      | NonNullable<NonNullable<Picker['sideContentV2']>['content']>
      | undefined
    showTags?: boolean
  }>(),
  {
    trackingModel: '',
    brand: '',
    content: undefined,
    showTags: true,
  },
)

const { trackClick } = useTracking()
const { grade } = useUrlParams()
const i18n = useI18n()

const currentSlideIndex = ref(0)
const loadedImageSlideIndex = ref<Record<number, boolean>>({})
const illustrations = ref<(typeof RevIllustration)[]>([])
const showSpinnerForCurrentSlide = ref(false)
let loaderDelayTimeoutId: ReturnType<typeof setTimeout> | null = null

function clearLoaderDelayTimeout() {
  if (loaderDelayTimeoutId !== null) {
    clearTimeout(loaderDelayTimeoutId)
    loaderDelayTimeoutId = null
  }
}

function scheduleLoaderForCurrentSlide() {
  clearLoaderDelayTimeout()
  showSpinnerForCurrentSlide.value = false
  const index = currentSlideIndex.value
  if (loadedImageSlideIndex.value[index] === true) {
    return
  }
  loaderDelayTimeoutId = setTimeout(() => {
    loaderDelayTimeoutId = null
    showSpinnerForCurrentSlide.value = true
  }, LOADER_DELAY_MS)
}

/** Sync loaded state from DOM (handles cached images that don't fire load, e.g. on initial page load). */
function syncLoadedStateFromDom() {
  const list = illustrations.value
  if (!list?.length) {
    return
  }
  for (let i = 0; i < list.length; i += 1) {
    const root = list[i]?.$el as HTMLElement | undefined
    const img =
      root?.tagName === 'IMG'
        ? (root as HTMLImageElement)
        : (root?.querySelector?.('img') as HTMLImageElement | null)
    if (img?.complete) {
      loadedImageSlideIndex.value[i] = true
    }
  }
}

onBeforeUnmount(clearLoaderDelayTimeout)

const slides = computed(() => {
  return (
    props.content?.find((entry) => entry.grade === grade.value)?.items || []
  )
})

watch(
  grade,
  () => {
    clearLoaderDelayTimeout()
    showSpinnerForCurrentSlide.value = false
    loadedImageSlideIndex.value = {}
    currentSlideIndex.value = 0
  },
  { immediate: false },
)

watch(
  [currentSlideIndex, slides],
  () => {
    scheduleLoaderForCurrentSlide()
  },
  { immediate: true },
)

// When slides are first rendered, sync loaded state from DOM so cached images show immediately (no load event on initial paint).
watch(
  slides,
  (newSlides) => {
    if (newSlides.length > 0) {
      nextTick(syncLoadedStateFromDom)
    }
  },
  { immediate: true },
)

// Whenever the current slide's image is loaded (by load event or DOM sync), hide the spinner.
watch(
  () => loadedImageSlideIndex.value[currentSlideIndex.value],
  (isLoaded) => {
    if (isLoaded) {
      clearLoaderDelayTimeout()
      showSpinnerForCurrentSlide.value = false
    }
  },
)

const shouldDisplayLoader = computed(() => {
  if (!grade.value) {
    return false
  }

  return showSpinnerForCurrentSlide.value
})

const isPremiumGrade = computed(() => {
  return grade.value === 9
})

function onSlideImpression({ index }: { index: number }) {
  if (index !== currentSlideIndex.value) {
    currentSlideIndex.value = index
    scheduleLoaderForCurrentSlide()
  }
  loadedImageSlideIndex.value[index] =
    loadedImageSlideIndex.value[index] ||
    illustrations.value?.[index].$el.complete
}

function onSlideImageLoaded(index: number) {
  loadedImageSlideIndex.value[index] = true
  if (index === currentSlideIndex.value) {
    clearLoaderDelayTimeout()
    showSpinnerForCurrentSlide.value = false
  }
}

function trackCarousel(name: string): void {
  const trackingValues: EventData = {
    name,
    zone: 'pp_step_grade_carousel',
    product_model: props.trackingModel,
  }

  if (grade.value) {
    trackingValues.current_grade = grade.value
  }

  trackClick(trackingValues)
}

// -- Animated chips --
const experiments = useExperiments()
const isWithOptimisedGrades = computed(() => {
  return experiments['experiment.ppOptimiseGrades'] === 'withOptimisedGrades'
})

const { onBeforeEnter, onEnter, onLeave } = useChipTransitionHooks()

// if we receive the Premium grade, we want to display the tags in a
// specific order (Premium tag last) regardless of the order they are
// sent by the backend. For the other grades, we keep the order sent
// by the backend. We know it's premium because we get `IconDiamond`
// in the icon property of the tag, so we can sort the tags based on that.
const sortedTags = computed(() => {
  if (!slides.value) {
    return []
  }

  if (!isPremiumGrade.value) {
    return slides.value[currentSlideIndex.value].tags
  }

  return [...slides.value[currentSlideIndex.value].tags].sort((a, b) => {
    if (a.icon === 'IconDiamond') {
      return 1
    }
    if (b.icon === 'IconDiamond') {
      return -1
    }

    return 0
  })
})

/**
 * Premium tags have `category_2_grade_9_section_Body_order_0`
 * and `category_2_grade_9_section_Body_order_1` ids, we want to display the one with
 * the highest order number (and the diamond icon) last, so we can apply
 * a specific style.
 */
const getLastPremiumTag = (tag: { id: string; icon?: string }) => {
  if (!isPremiumGrade.value || tag.icon !== 'IconDiamond') {
    return false
  }

  const diamondTags = slides.value[currentSlideIndex.value].tags.filter(
    (t) => t.icon === 'IconDiamond',
  )
  const highestOrderTag = diamondTags.reduce((highest, current) => {
    const currentOrder = parseInt(
      /order_(\d+)$/.exec(current.id)?.[1] ?? '-1',
      10,
    )
    const highestOrder = parseInt(
      /order_(\d+)$/.exec(highest.id)?.[1] ?? '-1',
      10,
    )

    return currentOrder > highestOrder ? current : highest
  })

  return highestOrderTag.id === tag.id
}
</script>

<style scoped>
.grade-gradient-unified {
  background: linear-gradient(200deg, #f6f0fc 10%, #e7dff3 90%);
}
</style>
